import { useEffect } from 'react';
import { useStore } from '@/store/useStore';
import { getUnifiedModule } from '@/utilities/logger/amplitude/initialise';

/** Amplitude's reserved exposure event, recognised by Experiment analysis. */
export const AMPLITUDE_EXPOSURE_EVENT = '$exposure';

/**
 * Where the variant stored in `amplitudeExperiments` came from. The store merges
 * both sources by key (last write wins), so the source is tracked per flag to
 * report exactly the variant that was served.
 */
export type ExperimentSource = 'sdk' | 'aggregation';

type StoredVariant = {
  key?: unknown;
  value?: unknown;
  expKey?: unknown;
  metadata?: { experimentKey?: unknown; default?: unknown };
};

const experimentSources = new Map<string, ExperimentSource>();
const trackedExposures = new Set<string>();

/**
 * Records the source of every flag in `variants`. Call it right before
 * `setAmplitudeExperiments` with the same payload so it mirrors the store merge.
 */
export const recordExperimentSource = (
  variants: Record<string, unknown> | null | undefined,
  source: ExperimentSource,
): void => {
  Object.keys(variants ?? {}).forEach((flagKey) =>
    experimentSources.set(flagKey, source),
  );
};

const toNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

const asStoredVariant = (entry: unknown): StoredVariant =>
  entry && typeof entry === 'object' ? (entry as StoredVariant) : {};

/** Amplitude analyses exposures by variant key; `value` is the legacy fallback. */
const resolveVariant = (entry: unknown): string | undefined => {
  if (typeof entry === 'string') {
    return toNonEmptyString(entry);
  }

  const { key, value } = asStoredVariant(entry);
  return toNonEmptyString(key) ?? toNonEmptyString(value);
};

const resolveExperimentKey = (entry: unknown): string | undefined => {
  const { expKey, metadata } = asStoredVariant(entry);
  return toNonEmptyString(metadata?.experimentKey) ?? toNonEmptyString(expKey);
};

/**
 * Fires one Amplitude `$exposure` for the variant the user is served for
 * `flagKey`, in every arm (control included). Call it where the flag decides
 * what the user gets.
 *
 * - SDK-sourced variants go through `experiment().exposure()`, which builds the
 *   payload and honours remote `trackExposure` and default-variant rules.
 * - Aggregation-sourced variants are tracked manually with the same payload.
 *
 * No-ops without a user, for missing or default (not enrolled) variants, and
 * for a flag/variant pair already exposed in this page load.
 */
export const trackExperimentExposure = (flagKey: string): void => {
  const { amplitudeExperiments, userSessionContext } = useStore.getState();
  const entry = amplitudeExperiments?.[flagKey];
  const variant = resolveVariant(entry);

  if (
    !userSessionContext.userId ||
    !variant ||
    asStoredVariant(entry).metadata?.default === true
  ) {
    return;
  }

  const exposureKey = `${flagKey}:${variant}`;
  if (trackedExposures.has(exposureKey)) {
    return;
  }
  trackedExposures.add(exposureKey);

  const experimentKey = resolveExperimentKey(entry);

  getUnifiedModule()
    .then((mod) => {
      if (experimentSources.get(flagKey) === 'sdk') {
        mod.experiment()?.exposure(flagKey);
        return;
      }

      mod.track(AMPLITUDE_EXPOSURE_EVENT, {
        flag_key: flagKey,
        variant,
        ...(experimentKey ? { experiment_key: experimentKey } : {}),
      });
    })
    .catch(() => {
      /* swallow — analytics must never break the app */
    });
};

export const trackExperimentExposures = (flagKeys: readonly string[]): void =>
  flagKeys.forEach(trackExperimentExposure);

/**
 * Hook form of {@link trackExperimentExposures} for render-time gates. Re-runs
 * when experiments or the user change, so a variant that arrives or changes
 * after mount is still exposed.
 */
export const useExperimentExposure = (...flagKeys: string[]): void => {
  const amplitudeExperiments = useStore((s) => s.amplitudeExperiments);
  const userId = useStore((s) => s.userSessionContext.userId);
  const flagKeysDep = flagKeys.join('|');

  useEffect(() => {
    trackExperimentExposures(flagKeysDep.split('|'));
  }, [amplitudeExperiments, userId, flagKeysDep]);
};

/** Test-only helper to reset module-level exposure state between tests. */
export const __resetExperimentExposureForTests = (): void => {
  experimentSources.clear();
  trackedExposures.clear();
};
